/**
 * The audio engine.
 *
 * Owns the AudioContext, the persistent graph, the transport clock and the
 * hot-swap machinery. Deliberately framework-free: nothing in here imports
 * React, and the worklets never see application state (spec 67/68).
 */

import type { CompiledSystem } from '../types/compiled';
import type { SystemSlotKey } from '../types/system';
import { ChainNodes, WORKLET_FILES, type StageName, type StageStatus } from './graph';

export type EngineEvent =
  | { type: 'state'; playing: boolean }
  | { type: 'position'; seconds: number; duration: number }
  | { type: 'status'; system: SystemSlotKey; status: StageStatus }
  | { type: 'meters'; data: MeterData }
  | { type: 'limiter'; engaged: boolean; gainDb: number; maxReductionDb: number }
  | { type: 'generator'; mode: string; progress: number; currentFreq: number }
  | { type: 'error'; message: string; fatal: boolean }
  | { type: 'log'; level: 'info' | 'warn' | 'error'; message: string }
  | { type: 'ended' };

export interface MeterData {
  peakL: number; peakR: number;
  peakHoldL: number; peakHoldR: number;
  rmsL: number; rmsR: number;
  momentary: number; shortTerm: number; integrated: number;
  correlation: number;
  clipCount: number;
  seconds: number;
  usingSab: boolean;
  frames?: Float32Array;
}

export interface EngineCapabilities {
  audioWorklet: boolean;
  sharedArrayBuffer: boolean;
  crossOriginIsolated: boolean;
  webgpu: boolean;
  wasm: boolean;
  wasmSimd: boolean;
  offlineAudioContext: boolean;
  decodeFlac: boolean | null;
  maxChannels: number;
  baseLatencySec: number | null;
  outputLatencySec: number | null;
  contextSampleRate: number;
  requestedSampleRate: number | null;
  /** True when the browser told us the device rate differs from the context. */
  resampledByBrowser: boolean | 'unknown';
}

export type TestSignalConfig = {
  mode: 'off' | 'sine' | 'sweep' | 'white' | 'pink' | 'impulse' | 'step' | 'multitone';
  level?: number;
  freq?: number;
  freq2?: number;
  sweepSeconds?: number;
  impulsePeriodMs?: number;
  channels?: 'both' | 'left' | 'right';
  multitone?: number[];
  restart?: boolean;
};

const RING_FRAMES = 32768;

export class AudioEngine {
  ctx: AudioContext | null = null;
  private chains: Record<SystemSlotKey, ChainNodes> | null = null;
  private sourceGain: GainNode | null = null;
  private master: GainNode | null = null;
  private limiter: AudioWorkletNode | null = null;
  private tap: AudioWorkletNode | null = null;
  private generator: AudioWorkletNode | null = null;
  private generatorGain: GainNode | null = null;

  private buffer: AudioBuffer | null = null;
  private source: AudioBufferSourceNode | null = null;

  private playing = false;
  private startedAtCtx = 0;
  private startOffset = 0;
  private loopEnabled = false;
  private loopStart = 0;
  private loopEnd = 0;
  private rafHandle: number | null = null;

  private listeners = new Set<(e: EngineEvent) => void>();
  private ringSab: SharedArrayBuffer | null = null;
  private ringCtrl: Int32Array | null = null;
  private ringData: Float32Array | null = null;
  private ringRead = 0;

  private activeSlot: SystemSlotKey = 'A';
  private abGainRampMs = 25;

  capabilities: EngineCapabilities = {
    audioWorklet: false,
    sharedArrayBuffer: false,
    crossOriginIsolated: false,
    webgpu: false,
    wasm: false,
    wasmSimd: false,
    offlineAudioContext: false,
    decodeFlac: null,
    maxChannels: 2,
    baseLatencySec: null,
    outputLatencySec: null,
    contextSampleRate: 0,
    requestedSampleRate: null,
    resampledByBrowser: 'unknown',
  };

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  on(fn: (e: EngineEvent) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(e: EngineEvent): void {
    for (const fn of this.listeners) {
      try {
        fn(e);
      } catch {
        /* a bad listener must never take down the engine */
      }
    }
  }

  private log(level: 'info' | 'warn' | 'error', message: string): void {
    this.emit({ type: 'log', level, message });
  }

  detectCapabilities(): EngineCapabilities {
    const c = this.capabilities;
    c.audioWorklet = typeof AudioWorkletNode !== 'undefined' && !!(window.AudioContext || (window as any).webkitAudioContext);
    c.sharedArrayBuffer = typeof SharedArrayBuffer !== 'undefined';
    c.crossOriginIsolated = typeof crossOriginIsolated !== 'undefined' ? crossOriginIsolated : false;
    c.webgpu = typeof navigator !== 'undefined' && 'gpu' in navigator;
    c.wasm = typeof WebAssembly !== 'undefined';
    c.offlineAudioContext = typeof OfflineAudioContext !== 'undefined';
    try {
      // Canonical SIMD feature-detection module.
      c.wasmSimd = WebAssembly.validate(
        new Uint8Array([
          0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10,
          10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11,
        ]),
      );
    } catch {
      c.wasmSimd = false;
    }
    return c;
  }

  /**
   * Create the context and build the graph.
   *
   * `preferredRate` follows the source file so the whole chain runs at the
   * file's native rate where the browser allows it. Changing it later requires
   * a new context, which is why it only ever happens on file load, never on a
   * hardware change.
   */
  async init(preferredRate?: number): Promise<void> {
    this.detectCapabilities();
    if (!this.capabilities.audioWorklet) {
      const msg = 'This browser does not support AudioWorklet. DAC ENGINE needs it for every DSP stage and cannot run without it.';
      this.emit({ type: 'error', message: msg, fatal: true });
      throw new Error(msg);
    }

    await this.teardown();

    const Ctor: typeof AudioContext = window.AudioContext || (window as any).webkitAudioContext;
    let ctx: AudioContext | null = null;
    if (preferredRate && preferredRate >= 8000 && preferredRate <= 768000) {
      try {
        ctx = new Ctor({ sampleRate: preferredRate, latencyHint: 'playback' });
        this.capabilities.requestedSampleRate = preferredRate;
      } catch {
        this.log('warn', `Browser refused an AudioContext at ${preferredRate} Hz; falling back to the default device rate.`);
      }
    }
    if (!ctx) {
      ctx = new Ctor({ latencyHint: 'playback' });
      this.capabilities.requestedSampleRate = preferredRate ?? null;
    }
    this.ctx = ctx;
    this.capabilities.contextSampleRate = ctx.sampleRate;
    this.capabilities.baseLatencySec = typeof ctx.baseLatency === 'number' ? ctx.baseLatency : null;
    this.capabilities.outputLatencySec = typeof (ctx as any).outputLatency === 'number' ? (ctx as any).outputLatency : null;
    this.capabilities.resampledByBrowser =
      preferredRate && preferredRate !== ctx.sampleRate ? true : 'unknown';
    this.capabilities.maxChannels = ctx.destination.maxChannelCount;

    for (const f of WORKLET_FILES) {
      try {
        await ctx.audioWorklet.addModule(f);
      } catch (err) {
        const msg = `Failed to load DSP module ${f}: ${(err as Error).message}`;
        this.emit({ type: 'error', message: msg, fatal: true });
        throw new Error(msg);
      }
    }

    this.buildGraph();
    this.log('info', `AudioContext running at ${ctx.sampleRate} Hz, base latency ${((this.capabilities.baseLatencySec ?? 0) * 1000).toFixed(2)} ms.`);
  }

  private buildGraph(): void {
    const ctx = this.ctx!;

    this.sourceGain = ctx.createGain();
    this.sourceGain.gain.value = 1;

    this.master = ctx.createGain();
    this.master.gain.value = Math.pow(10, -12 / 20);

    const onStatus = (system: SystemSlotKey, status: StageStatus) =>
      this.emit({ type: 'status', system, status });
    const onError = (system: SystemSlotKey, stage: string, message: string) => {
      this.log('error', `[${system}/${stage}] ${message}`);
      this.emit({ type: 'error', message: `${system} ${stage}: ${message}`, fatal: false });
    };

    this.chains = {
      A: new ChainNodes(ctx, 'A', onStatus, onError),
      B: new ChainNodes(ctx, 'B', onStatus, onError),
    };

    this.sourceGain.connect(this.chains.A.input);
    this.sourceGain.connect(this.chains.B.input);

    // Analyser tap sits after the A/B mix so it always shows what is audible.
    this.tap = new AudioWorkletNode(ctx, 'analyser-tap', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [2],
      channelCount: 2,
      channelCountMode: 'explicit',
      processorOptions: { name: 'main', ...this.makeRing() },
    });
    this.tap.port.onmessage = (e) => {
      const m = e.data;
      if (m && m.type === 'meters') this.emit({ type: 'meters', data: m as MeterData });
    };

    this.limiter = new AudioWorkletNode(ctx, 'safety-limiter', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [2],
      channelCount: 2,
      channelCountMode: 'explicit',
      processorOptions: { enabled: true, ceilingDb: -0.3 },
    });
    this.limiter.port.onmessage = (e) => {
      const m = e.data;
      if (m && m.type === 'limiter') {
        this.emit({ type: 'limiter', engaged: m.engaged, gainDb: m.gainDb, maxReductionDb: m.maxReductionDb });
      }
    };

    this.generator = new AudioWorkletNode(ctx, 'test-generator', {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [2],
    });
    this.generator.port.onmessage = (e) => {
      const m = e.data;
      if (m && m.type === 'generator') {
        this.emit({ type: 'generator', mode: m.mode, progress: m.progress, currentFreq: m.currentFreq });
      }
    };
    this.generatorGain = ctx.createGain();
    this.generatorGain.gain.value = 1;
    this.generator.connect(this.generatorGain).connect(this.sourceGain);

    // Single-system is the default, so chain B sleeps until it is needed.
    this.chains.B.setIdle(true);

    this.chains.A.gain.connect(this.tap);
    this.chains.B.gain.connect(this.tap);
    this.tap.connect(this.master);
    this.master.connect(this.limiter);
    this.limiter.connect(ctx.destination);
  }

  private makeRing(): { sab?: SharedArrayBuffer; ringFrames?: number } {
    if (!this.capabilities.sharedArrayBuffer || !this.capabilities.crossOriginIsolated) {
      this.log(
        'info',
        'SharedArrayBuffer is unavailable (the page is not cross-origin isolated). The analyser falls back to postMessage, which works identically but costs a little more main-thread time.',
      );
      return {};
    }
    try {
      const sab = new SharedArrayBuffer(32 + RING_FRAMES * 2 * 4);
      this.ringSab = sab;
      this.ringCtrl = new Int32Array(sab, 0, 8);
      this.ringData = new Float32Array(sab, 32, RING_FRAMES * 2);
      this.ringRead = 0;
      return { sab, ringFrames: RING_FRAMES };
    } catch {
      return {};
    }
  }

  /** Pull whatever the worklet has written since the last call. */
  readRing(out: Float32Array): number {
    if (!this.ringCtrl || !this.ringData) return 0;
    const write = Atomics.load(this.ringCtrl, 0);
    const frames = RING_FRAMES;
    let available = write - this.ringRead;
    if (available < 0) available += frames;
    const want = Math.min(available, out.length / 2);
    if (want <= 0) return 0;
    // Read the newest `want` frames.
    let read = write - want;
    if (read < 0) read += frames;
    for (let i = 0; i < want; i++) {
      const idx = (read + i) % frames;
      out[i * 2] = this.ringData[idx * 2];
      out[i * 2 + 1] = this.ringData[idx * 2 + 1];
    }
    this.ringRead = write;
    return want;
  }

  async teardown(): Promise<void> {
    this.stopPositionLoop();
    if (this.source) {
      try { this.source.onended = null; this.source.stop(); } catch { /* already stopped */ }
      this.source.disconnect();
      this.source = null;
    }
    if (this.chains) {
      this.chains.A.disconnect();
      this.chains.B.disconnect();
      this.chains = null;
    }
    for (const n of [this.tap, this.limiter, this.generator, this.generatorGain, this.master, this.sourceGain]) {
      try { n?.disconnect(); } catch { /* not connected */ }
    }
    if (this.tap) this.tap.port.onmessage = null;
    if (this.limiter) this.limiter.port.onmessage = null;
    if (this.generator) this.generator.port.onmessage = null;
    this.tap = null;
    this.limiter = null;
    this.generator = null;
    this.generatorGain = null;
    this.master = null;
    this.sourceGain = null;
    this.ringSab = null;
    this.ringCtrl = null;
    this.ringData = null;
    if (this.ctx) {
      const dying = this.ctx;
      this.ctx = null;
      // Do NOT block on close(). On some platforms closing an AudioContext
      // waits on the audio device, and a device that is slow to release would
      // otherwise stall the caller indefinitely. Abandoning the reference is
      // enough for it to be collected; the race just stops us waiting forever.
      try {
        await Promise.race([
          dying.close().catch(() => undefined),
          new Promise<void>((r) => setTimeout(r, 1500)),
        ]);
      } catch {
        /* nothing useful to do; the context is already unreferenced */
      }
    }
    this.playing = false;
  }

  async resume(): Promise<void> {
    if (this.ctx && this.ctx.state === 'suspended') await this.ctx.resume();
  }

  get ready(): boolean {
    return !!this.ctx && !!this.chains;
  }

  get sampleRate(): number {
    return this.ctx?.sampleRate ?? 0;
  }

  // -------------------------------------------------------------------------
  // Source
  // -------------------------------------------------------------------------

  setBuffer(buffer: AudioBuffer | null): void {
    const wasPlaying = this.playing;
    if (this.source) {
      try { this.source.onended = null; this.source.stop(); } catch { /* fine */ }
      this.source.disconnect();
      this.source = null;
    }
    this.buffer = buffer;
    this.startOffset = 0;
    this.loopStart = 0;
    this.loopEnd = buffer?.duration ?? 0;
    this.playing = false;
    if (wasPlaying) this.emit({ type: 'state', playing: false });
    this.emit({ type: 'position', seconds: 0, duration: buffer?.duration ?? 0 });
  }

  get duration(): number {
    return this.buffer?.duration ?? 0;
  }

  get position(): number {
    if (!this.ctx || !this.buffer) return this.startOffset;
    if (!this.playing) return this.startOffset;
    const t = this.startOffset + (this.ctx.currentTime - this.startedAtCtx);
    if (this.loopEnabled && this.loopEnd > this.loopStart) {
      const span = this.loopEnd - this.loopStart;
      if (t >= this.loopEnd) return this.loopStart + ((t - this.loopStart) % span);
    }
    return Math.min(t, this.buffer.duration);
  }

  play(): void {
    if (!this.ctx || !this.buffer || this.playing) return;
    void this.resume();
    this.startSource(this.startOffset);
    this.playing = true;
    this.emit({ type: 'state', playing: true });
    this.startPositionLoop();
  }

  private startSource(offset: number): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.buffer;
    src.loop = this.loopEnabled;
    if (this.loopEnabled) {
      src.loopStart = this.loopStart;
      src.loopEnd = this.loopEnd;
    }
    src.connect(this.sourceGain!);
    src.onended = () => {
      // Only a natural end should stop the transport; a seek replaces the node
      // deliberately and clears onended first.
      if (this.playing && !this.loopEnabled) {
        this.playing = false;
        this.startOffset = this.buffer?.duration ?? 0;
        this.stopPositionLoop();
        this.emit({ type: 'state', playing: false });
        this.emit({ type: 'ended' });
      }
    };
    // Short fade-in on the source gain removes the discontinuity of starting
    // mid-waveform. This is a SOURCE-level operation; it never happens on a
    // hardware change.
    const g = this.sourceGain!.gain;
    const now = ctx.currentTime;
    g.cancelScheduledValues(now);
    g.setValueAtTime(0, now);
    g.linearRampToValueAtTime(1, now + 0.006);
    src.start(now, Math.min(offset, this.buffer!.duration));
    this.startedAtCtx = now;
    this.startOffset = offset;
    this.source = src;
  }

  pause(): void {
    if (!this.playing || !this.ctx) return;
    const pos = this.position;
    this.fadeOutAndStop();
    this.startOffset = pos;
    this.playing = false;
    this.stopPositionLoop();
    this.emit({ type: 'state', playing: false });
    this.emit({ type: 'position', seconds: pos, duration: this.duration });
  }

  private fadeOutAndStop(): void {
    const ctx = this.ctx!;
    const g = this.sourceGain!.gain;
    const now = ctx.currentTime;
    g.cancelScheduledValues(now);
    g.setValueAtTime(g.value, now);
    g.linearRampToValueAtTime(0, now + 0.006);
    const src = this.source;
    if (src) {
      src.onended = null;
      try { src.stop(now + 0.02); } catch { /* already stopped */ }
      setTimeout(() => { try { src.disconnect(); } catch { /* fine */ } }, 60);
    }
    this.source = null;
  }

  stop(): void {
    if (this.source) this.fadeOutAndStop();
    this.playing = false;
    this.startOffset = 0;
    this.stopPositionLoop();
    this.emit({ type: 'state', playing: false });
    this.emit({ type: 'position', seconds: 0, duration: this.duration });
  }

  seek(seconds: number): void {
    if (!this.buffer) return;
    const target = Math.max(0, Math.min(seconds, this.buffer.duration));
    if (this.playing) {
      this.fadeOutAndStop();
      this.startSource(target);
    } else {
      this.startOffset = target;
    }
    this.emit({ type: 'position', seconds: target, duration: this.duration });
  }

  setLoop(enabled: boolean, start?: number, end?: number): void {
    this.loopEnabled = enabled;
    if (start !== undefined) this.loopStart = start;
    if (end !== undefined) this.loopEnd = end;
    if (this.source) {
      this.source.loop = enabled;
      this.source.loopStart = this.loopStart;
      this.source.loopEnd = this.loopEnd > this.loopStart ? this.loopEnd : this.duration;
    }
  }

  get isPlaying(): boolean {
    return this.playing;
  }

  /**
   * Real-time health watchdog (spec 35).
   *
   * If the DSP cannot meet the render deadline, the AudioContext clock falls
   * behind wall clock -- that is the definitive symptom, and it is measurable
   * without any per-worklet instrumentation (which is not universally
   * available anyway). When it happens the engine says so and asks to be
   * stepped down a tier rather than glitching silently.
   *
   * Only measured while the page is VISIBLE: browsers legitimately throttle
   * audio in a backgrounded tab, and degrading quality because someone changed
   * tabs would be a bug, not a feature.
   */
  onOverload: ((detail: { ratio: number; seconds: number }) => void) | null = null;
  private healthCtxTime = 0;
  private healthWall = 0;
  private healthBad = 0;

  private startPositionLoop(): void {
    if (this.rafHandle !== null) return;
    this.healthCtxTime = this.ctx?.currentTime ?? 0;
    this.healthWall = performance.now();
    this.healthBad = 0;
    const tick = () => {
      this.rafHandle = requestAnimationFrame(tick);
      this.emit({ type: 'position', seconds: this.position, duration: this.duration });
      this.checkHealth();
    };
    this.rafHandle = requestAnimationFrame(tick);
  }

  private checkHealth(): void {
    if (!this.ctx || !this.playing) return;
    const wall = performance.now();
    const dtWall = (wall - this.healthWall) / 1000;
    if (dtWall < 1.0) return;
    const dtCtx = this.ctx.currentTime - this.healthCtxTime;
    this.healthWall = wall;
    this.healthCtxTime = this.ctx.currentTime;

    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      this.healthBad = 0;
      return;
    }
    const ratio = dtCtx / dtWall;
    this.lastHealthRatio = ratio;
    // Below 0.92 the context is measurably losing time; require it to persist
    // so a single scheduling hiccup does not trigger a quality change.
    if (ratio < 0.92) {
      this.healthBad++;
      if (this.healthBad >= 3) {
        this.healthBad = 0;
        this.onOverload?.({ ratio, seconds: dtWall });
      }
    } else {
      this.healthBad = 0;
    }
  }

  /** Most recent context-clock / wall-clock ratio. 1.0 is healthy. */
  lastHealthRatio = 1;

  private stopPositionLoop(): void {
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
  }

  // -------------------------------------------------------------------------
  // Hardware
  // -------------------------------------------------------------------------

  /**
   * Apply a compiled chain. This is the hot-swap entry point: nothing is
   * disconnected, nothing is recreated, and the transport is not touched.
   */
  applySystem(key: SystemSlotKey, sys: CompiledSystem, transitionMs: number, only?: StageName[]): void {
    this.chains?.[key].applySystem(sys, transitionMs, only);
  }

  setBypass(key: SystemSlotKey, stage: StageName, bypassed: boolean): void {
    this.chains?.[key].setBypass(stage, bypassed);
  }

  setStageParam(key: SystemSlotKey, stage: StageName, name: string, value: number): void {
    this.chains?.[key].setParam(stage, name, value);
  }

  /**
   * Crossfade between the two complete systems.
   *
   * The incoming chain is woken BEFORE the crossfade starts so its filters have
   * warmed up by the time it is audible, and the outgoing chain is put back to
   * sleep only once the crossfade has finished.
   */
  setActiveSystem(key: SystemSlotKey, rampMs = this.abGainRampMs): void {
    if (!this.ctx || !this.chains) return;
    this.activeSlot = key;
    this.chains[key].setIdle(false);
    const now = this.ctx.currentTime;
    const ramp = Math.max(0.001, rampMs / 1000);
    for (const k of ['A', 'B'] as SystemSlotKey[]) {
      const g = this.chains[k].gain.gain;
      const target = k === key ? 1 : 0;
      g.cancelScheduledValues(now);
      g.setValueAtTime(g.value, now);
      // Equal-power-ish: a linear ramp on both sides of a hard A/B is fine
      // because only one side is ever non-zero at steady state.
      g.linearRampToValueAtTime(target, now + ramp);
    }
    if (this.idleTimer !== null) clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => {
      this.idleTimer = null;
      if (!this.chains || !this.comparisonActive) return;
      // Only sleep the other chain when comparison is single-system.
      if (this.singleSystem) {
        const other: SystemSlotKey = key === 'A' ? 'B' : 'A';
        this.chains[other].setIdle(true);
      }
    }, rampMs + 120);
  }

  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private comparisonActive = true;
  private singleSystem = true;

  /**
   * Tell the engine whether the second chain is in use. In single-system mode
   * the inactive chain sleeps; in A/B, ABX or bank mode both stay awake so a
   * switch is instant and already warm.
   */
  setSingleSystem(single: boolean): void {
    this.singleSystem = single;
    if (!this.chains) return;
    if (single) {
      const other: SystemSlotKey = this.activeSlot === 'A' ? 'B' : 'A';
      this.chains[this.activeSlot].setIdle(false);
      this.chains[other].setIdle(true);
    } else {
      this.chains.A.setIdle(false);
      this.chains.B.setIdle(false);
    }
  }

  get currentSystem(): SystemSlotKey {
    return this.activeSlot;
  }

  setAbRampMs(ms: number): void {
    this.abGainRampMs = ms;
  }

  /** Per-system output trim, used by the level-matching engine. */
  setSystemTrim(key: SystemSlotKey, db: number): void {
    if (!this.chains || !this.ctx) return;
    // Trim rides on the output stage so it is inside the A/B crossfade.
    this.chains[key].setParam('output', 'trimDb', db);
  }

  setMasterGainDb(db: number, rampMs = 20): void {
    if (!this.master || !this.ctx) return;
    const now = this.ctx.currentTime;
    const g = this.master.gain;
    g.cancelScheduledValues(now);
    g.setValueAtTime(g.value, now);
    g.linearRampToValueAtTime(Math.pow(10, db / 20), now + rampMs / 1000);
  }

  setMuted(muted: boolean, gainDb: number): void {
    this.setMasterGainDb(muted ? -200 : gainDb, 12);
  }

  setLimiter(enabled: boolean, ceilingDb = -0.3): void {
    this.limiter?.port.postMessage({ type: 'enable', value: enabled });
    this.limiter?.port.postMessage({ type: 'ceiling', value: ceilingDb });
  }

  resetMeters(): void {
    this.tap?.port.postMessage({ type: 'reset-peak' });
    this.tap?.port.postMessage({ type: 'reset-integrated' });
    this.limiter?.port.postMessage({ type: 'reset' });
  }

  setTestSignal(cfg: TestSignalConfig): void {
    this.generator?.port.postMessage({ type: 'config', config: cfg });
    // Mute the file source while a test signal runs so they do not sum.
    if (this.sourceGain && this.ctx) {
      const active = cfg.mode !== 'off';
      if (active && this.playing) this.pause();
    }
  }

  /** Ask every stage for a fresh status report. */
  pollStatus(): void {
    if (!this.chains) return;
    for (const key of ['A', 'B'] as SystemSlotKey[]) {
      for (const [, node] of this.chains[key].entries()) node.port.postMessage({ type: 'query' });
    }
  }

  getStageStatus(key: SystemSlotKey): Map<string, StageStatus> | null {
    return this.chains?.[key].status ?? null;
  }
}

export const engine = new AudioEngine();
